"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { micromark } from "micromark";
import DOMPurify from "dompurify";
import type { DesignConversationEvent } from "@open-design/contracts";
import { uploadDesignAsset } from "./api";
import { Button, Textarea } from "./ui";
import {
  ArrowDown,
  ArrowUp,
  FileText,
  LoaderCircle,
  Plus,
  Target,
  X,
} from "lucide-react";
import css from "./EditorChat.module.css";

export interface ChatAttachment {
  key: string;
  file: File;
  preview?: string;
  id?: string;
  status: "uploading" | "ready" | "failed";
  error?: string;
}
const types = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "text/plain",
]);
export function useChatAttachments(
  websiteId: string,
  org: number,
  csrf: string,
) {
  const [files, setFiles] = useState<ChatAttachment[]>([]);
  const current = useRef<ChatAttachment[]>([]);
  const generation = useRef(0);
  const [error, setError] = useState("");
  const update = (next: ChatAttachment[]) => {
    current.current = next;
    setFiles(next);
  };
  const clear = () => {
    for (const file of current.current)
      if (file.preview) URL.revokeObjectURL(file.preview);
    update([]);
  };
  useEffect(() => {
    generation.current += 1;
    current.current = [];
    setFiles([]);
    setError("");
    return () => {
      generation.current += 1;
      for (const file of current.current)
        if (file.preview) URL.revokeObjectURL(file.preview);
      current.current = [];
    };
  }, [websiteId, org]);
  const upload = async (file: ChatAttachment) => {
    const epoch = generation.current;
    update(
      current.current.map((item) =>
        item.key === file.key
          ? { ...item, status: "uploading", error: "" }
          : item,
      ),
    );
    try {
      const asset = await uploadDesignAsset(websiteId, file.file, org, csrf);
      if (epoch === generation.current)
        update(
          current.current.map((item) =>
            item.key === file.key
              ? { ...item, id: asset.id, status: "ready" }
              : item,
          ),
        );
    } catch {
      if (epoch === generation.current)
        update(
          current.current.map((item) =>
            item.key === file.key
              ? { ...item, status: "failed", error: "Upload failed" }
              : item,
          ),
        );
    }
  };
  return {
    files,
    error,
    clear,
    retry: upload,
    remove: (key: string) => {
      const file = current.current.find((item) => item.key === key);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      update(current.current.filter((item) => item.key !== key));
    },
    add: (incoming: File[]) => {
      setError("");
      for (const file of incoming) {
        if (current.current.length >= 12) {
          setError("Attach up to 12 files.");
          break;
        }
        if (!types.has(file.type) || file.size > 10 * 1024 * 1024) {
          setError("Use JPG, PNG, WebP, PDF, or text files up to 10 MiB.");
          continue;
        }
        const item: ChatAttachment = {
          key: crypto.randomUUID(),
          file,
          status: "uploading",
          ...(file.type.startsWith("image/") &&
          typeof URL.createObjectURL === "function"
            ? { preview: URL.createObjectURL(file) }
            : {}),
        };
        update([...current.current, item]);
        void upload(item);
      }
    },
  };
}

function AssistantText({ text }: { text: string }) {
  const html = useMemo(
    () =>
      typeof window === "undefined"
        ? null
        : DOMPurify.sanitize(micromark(text), {
            ALLOWED_TAGS: [
              "p",
              "strong",
              "em",
              "code",
              "pre",
              "ul",
              "ol",
              "li",
              "blockquote",
              "br",
              "hr",
              "h1",
              "h2",
              "h3",
              "a",
            ],
            ALLOWED_ATTR: ["href", "title"],
          }).replaceAll("<a ", '<a target="_blank" rel="noopener noreferrer" '),
    [text],
  );
  return html === null ? (
    <p>{text}</p>
  ) : (
    <div className={css.markdown} dangerouslySetInnerHTML={{ __html: html }} />
  );
}

export function EditorChat({
  events,
  prompt,
  onPrompt,
  onSend,
  disabled,
  canSend,
  attachments,
  context,
  onClearContext,
  question,
  draftBlocked,
  active = true,
  workingLabel,
  unavailableReason,
  creditNotice,
}: {
  events: DesignConversationEvent[];
  prompt: string;
  onPrompt: (value: string) => void;
  onSend: () => void;
  disabled: boolean;
  canSend: boolean;
  attachments: ReturnType<typeof useChatAttachments>;
  context?: string;
  onClearContext: () => void;
  question?: string;
  draftBlocked: boolean;
  active?: boolean;
  pageLabel?: string;
  workingLabel?: string;
  unavailableReason?: string;
  creditNotice?: string;
}) {
  const canSubmit =
    canSend && !disabled && !unavailableReason && Boolean(prompt.trim());
  const thread = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  const input = useRef<HTMLTextAreaElement>(null);
  const chooser = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  useLayoutEffect(() => {
    if (active && following.current && thread.current)
      thread.current.scrollTop = thread.current.scrollHeight;
  }, [events, question, active, workingLabel]);
  useLayoutEffect(() => {
    if (input.current) {
      input.current.style.height = "auto";
      input.current.style.height = `${Math.min(160, Math.max(44, input.current.scrollHeight))}px`;
    }
  }, [prompt, active]);
  return (
    <section
      className={`${css.chat} ${dragging ? css.dragging : ""}`}
      aria-label="Website chat"
      onDragEnter={(event) => {
        if (!disabled && event.dataTransfer.types.includes("Files")) {
          event.preventDefault();
          dragDepth.current++;
          setDragging(true);
        }
      }}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) event.preventDefault();
      }}
      onDragLeave={() => {
        if (--dragDepth.current <= 0) {
          dragDepth.current = 0;
          setDragging(false);
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (!disabled) attachments.add(Array.from(event.dataTransfer.files));
      }}
    >
      <div
        ref={thread}
        className={css.thread}
        role="log"
        aria-label="Conversation"
        aria-live="polite"
        onScroll={() => {
          const node = thread.current;
          if (node) {
            following.current =
              node.scrollHeight - node.scrollTop - node.clientHeight < 64;
            setAtBottom(following.current);
          }
        }}
      >
        {events.map((event) => (
          <article
            key={event.id}
            className={event.kind === "user" ? css.user : css.assistant}
          >
            {event.kind === "user" ? (
              <p>{event.payload.text}</p>
            ) : (
              <AssistantText text={event.payload.text ?? ""} />
            )}
            {!!event.payload.attachments?.length && (
              <div className={css.sentFiles}>
                {event.payload.attachments.map((id) => (
                  <span key={id}>
                    <FileText size={13} />
                    {event.attachment_details?.find((file) => file.id === id)
                      ?.filename ?? "Attached reference"}
                  </span>
                ))}
              </div>
            )}
          </article>
        ))}
        {question &&
          !events.some((event) => event.payload.text === question) && (
            <article className={css.assistant}>
              <AssistantText text={question} />
            </article>
          )}
        {workingLabel && (
          <div className={css.working} role="status">
            <LoaderCircle size={15} />
            <span>{workingLabel}</span>
          </div>
        )}
      </div>
      {!atBottom && (
        <div className={css.jump}>
          <button
            type="button"
            onClick={() => {
              if (thread.current)
                thread.current.scrollTop = thread.current.scrollHeight;
              following.current = true;
              setAtBottom(true);
            }}
          >
            <ArrowDown size={14} />
            Jump to latest
          </button>
        </div>
      )}
      <div className={css.composerArea}>
        {dragging && (
          <div className={css.dropNotice} aria-hidden="true">
            Drop references to attach
          </div>
        )}
        <form
          className={css.composer}
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) onSend();
          }}
        >
          {context && (
            <div className={css.context}>
              <Target size={14} />
              <span title={context}>{context}</span>
              <Button
                type="button"
                aria-label="Remove selected context"
                onClick={onClearContext}
              >
                <X size={13} />
              </Button>
            </div>
          )}
          {!!attachments.files.length && (
            <div className={css.attachments}>
              {attachments.files.map((file) => (
                <div key={file.key} className={css.attachment}>
                  {file.preview ? (
                    <img src={file.preview} alt="" />
                  ) : (
                    <span className={css.fileIcon}>
                      <FileText size={18} />
                    </span>
                  )}
                  <span title={file.file.name}>{file.file.name}</span>
                  <small>
                    {file.status === "uploading" ? "Uploading…" : file.error}
                  </small>
                  {file.status === "failed" && (
                    <Button
                      type="button"
                      onClick={() => void attachments.retry(file)}
                    >
                      Retry
                    </Button>
                  )}
                  <Button
                    type="button"
                    aria-label={`Remove ${file.file.name}`}
                    onClick={() => attachments.remove(file.key)}
                  >
                    <X size={13} />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Textarea
            ref={input}
            aria-label={question ? "Your answer" : "Message Buildy"}
            placeholder={
              question ? "Write your answer…" : "What would you like to change?"
            }
            value={prompt}
            rows={1}
            disabled={disabled}
            onChange={(event) => onPrompt(event.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing &&
                event.keyCode !== 229
              ) {
                event.preventDefault();
                if (canSubmit) onSend();
              }
            }}
          />
          <div className={css.composerActions}>
            <input
              ref={chooser}
              type="file"
              multiple
              hidden
              accept="image/jpeg,image/png,image/webp,application/pdf,text/plain"
              onChange={(event) => {
                attachments.add(Array.from(event.target.files ?? []));
                event.target.value = "";
              }}
            />
            <Button
              type="button"
              aria-label="Attach files"
              title="Attach files"
              disabled={disabled || attachments.files.length >= 12}
              onClick={() => chooser.current?.click()}
            >
              <Plus size={16} />
            </Button>
            <Button
              type="submit"
              data-ready={canSend && !disabled && Boolean(prompt.trim())}
              aria-label={question ? "Send answer" : "Send message"}
              disabled={!canSubmit}
              title={
                unavailableReason ?? (question ? "Send answer" : "Send message")
              }
            >
              <ArrowUp size={16} />
            </Button>
          </div>
          {attachments.error && (
            <p role="alert" className={css.help}>
              {attachments.error}
            </p>
          )}
          {creditNotice && (
            <p role="status" className={css.help}>
              {creditNotice}
            </p>
          )}
          {draftBlocked && (
            <p className={css.help}>
              Save or discard your edits before asking AI.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
